import { Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'src/components/Toast';
import Toggle from 'src/components/Toggle';
import { useConfiguration } from 'src/providers/Configuration';
import { useTenant } from 'src/providers/Tenant';
import { Configuration, patchConfiguration, PostProcessing } from 'src/services/productInventory';

interface PostProcessingModeToggleProps {
  configurationPID: string;
}

export function PostProcessingModeToggle({ configurationPID }: Readonly<PostProcessingModeToggleProps>) {
  const { accountId } = useTenant();
  const queryClient = useQueryClient();
  const { postProcessing } = useConfiguration();
  const [isPostProcessingAdvanced, setIsPostProcessingAdvanced] = useState(
    postProcessing === undefined || postProcessing === PostProcessing.ADVANCED
  );
  const { t } = useTranslation('manageProduct');

  const {
    mutate: handlePostProcessingMode,
    isLoading: isUpdatingPostProcessingMode,
    isError: isErrorPostProcessingMode,
  } = useMutation(
    async (body: Partial<Configuration & { postProcessing: PostProcessing.ADVANCED | PostProcessing.BASIC }>) => {
      await patchConfiguration(configurationPID, body, accountId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getConfiguration']);
        queryClient.invalidateQueries(['productList']);
      },
    }
  );

  const handlePostProcessingToggleChange = async (e: MouseEvent) => {
    const postProcessingMode = (e.target as HTMLInputElement).checked ? PostProcessing.ADVANCED : PostProcessing.BASIC;
    handlePostProcessingMode({ postProcessing: postProcessingMode });
    setIsPostProcessingAdvanced((prevState) => !prevState);
  };

  const isDeterminingInitialPostProcessing = postProcessing === undefined;

  /* istanbul ignore next - not sure we do need this really */
  if (isDeterminingInitialPostProcessing) {
    return null;
  }

  return (
    <>
      <Toggle
        loading={isUpdatingPostProcessingMode}
        checked={isPostProcessingAdvanced}
        label={t('productOverview.turnOnPostProcessing')}
        states={{ checkedState: 'Yes', unCheckedState: 'No' }}
        onClick={handlePostProcessingToggleChange}
        id="productPostProcessing"
      />
      <Typography>{t('productOverview.productPostProcessing')}</Typography>

      <Toast
        dependency={isErrorPostProcessingMode}
        severity="error"
        title={t('productOverview.postProcessingUpdateFailed')}
      />
    </>
  );
}
